import { useCallback, useEffect, useRef } from 'react';
import Quill from 'quill';
import type Toolbar from 'quill/modules/toolbar';
import 'quill/dist/quill.snow.css';

type Props = {
  value: string;
  onChange: (html: string) => void;
  modules?: Record<string, unknown>;
  formats?: string[];
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  readOnly?: boolean;
};

// Quill 2 inserts the toolbar before the container, outside the node React
// owns, so React leaves it behind on unmount. Remove it with the editor.
const removeToolbar = (editor: Quill) => {
  const toolbar = editor.getModule('toolbar') as Toolbar | undefined;
  toolbar?.container?.remove();
};

// lastHtmlRef guard rationale: Quill's text-change listener fires on
// programmatic setContents too. Without this guard, parent state updates
// round-trip back to the editor's onChange and pin Quill's internal
// `this.value` to a normalized-empty form when the initial value is ''.
export const QuillEditor = (props: Props) => {
  const { value, onChange, modules, formats, className, style, placeholder, readOnly } = props;

  const editorRef = useRef<Quill | null>(null);
  const lastHtmlRef = useRef<string>(value);
  const propsRef = useRef(props);
  propsRef.current = props;

  // Ref callback so React StrictMode's double-invoke is handled at the DOM
  // attach point. We initialise Quill exactly once per attached DOM node
  // (keyed by editorRef.current) and tear down on detach.
  const refCallback = useCallback((el: HTMLDivElement | null) => {
    if (el === null) {
      const editor = editorRef.current;
      if (editor) {
        editor.off('text-change');
        removeToolbar(editor);
      }
      editorRef.current = null;
      return;
    }
    if (editorRef.current) return;
    // Belt-and-suspenders against any pre-existing Quill DOM that survived
    // a parent re-render: strip ql-* elements before instantiating.
    el.querySelectorAll('.ql-toolbar, .ql-editor').forEach(node => node.remove());
    el.innerHTML = '';
    const { modules, formats, placeholder, readOnly, value } = propsRef.current;
    const editor = new Quill(el, {
      theme: 'snow',
      modules: modules ?? {},
      formats: formats ?? [],
      placeholder,
      readOnly
    });
    editorRef.current = editor;
    if (value && value.trim().length > 0) {
      editor.setContents(editor.clipboard.convert({ html: value }), 'silent');
    }
    const handler = () => {
      const html = editor.root.innerHTML;
      if (html !== lastHtmlRef.current) {
        lastHtmlRef.current = html;
        propsRef.current.onChange(html);
      }
    };
    editor.on('text-change', handler);
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || value === lastHtmlRef.current) return;
    lastHtmlRef.current = value;
    editor.setContents(editor.clipboard.convert({ html: value || '' }), 'silent');
  }, [value]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.enable(!(readOnly ?? false));
  }, [readOnly]);

  return <div ref={refCallback} className={className} style={style} />;
};
